import { AxiosResponse } from 'axios';
import { apiClient } from '../../apiHelper';
import { useFetch, useGenericMutation } from '../react-query';
export const mwlQueryKeys = {
  all: ['mwl'],
  worklist: (params: Record<string, unknown>) => ['mwl', 'worklist', JSON.stringify(params ?? {})],
  order: (orderId: number) => ['mwl', 'order', String(orderId)],
  orders: (params: Record<string, unknown>) => ['mwl', 'orders', JSON.stringify(params ?? {})],
  patients: (params: Record<string, unknown>) => ['mwl', 'patients', JSON.stringify(params ?? {})],
  providers: ['mwl', 'application-entities'],
  stats: ['mwl', 'stats'],
};

export type SpsStatus =
  | 'SCHEDULED'
  | 'ARRIVED'
  | 'READY'
  | 'STARTED'
  | 'COMPLETED'
  | 'DISCONTINUED';

export type OrderStatus =
  | 'SCHEDULED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'DISCONTINUED'
  | 'CANCELLED';

export interface MwlPatient extends Record<string, unknown> {
  id?: number;
  patientId: string;
  issuerOfPatientId?: string | null;
  patientName?: string | null;
  patientBirthDate?: string | null;
  patientSex?: string | null;
  patientWeight?: number | null;
  patientSize?: number | null;
  pregnancyStatus?: number | null;
  medicalAlerts?: string | null;
  allergies?: string | null;
  otherPatientIds?: string | null;
  patientAddress?: string | null;
  patientTelephoneNumbers?: string | null;
  patientComments?: string | null;
  source?: 'MANUAL' | 'HL7' | 'DICOM_MWL' | 'DICOM_STORE';
  active?: boolean;
  mergedIntoId?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface MwlScheduledStep extends Record<string, unknown> {
  id?: number;
  orderId?: number;
  spsId?: string | null;
  modality: string;
  scheduledStationAeTitle?: string | null;
  scheduledStationName?: string | null;
  startDate?: string | null;
  startTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
  durationMinutes?: number | null;
  performingPhysicianName?: string | null;
  description?: string | null;
  location?: string | null;
  preMedication?: string | null;
  comments?: string | null;
  protocolCodeValue?: string | null;
  protocolCodeMeaning?: string | null;
  requestedContrastAgent?: string | null;
  status?: SpsStatus;
  discontinuationReason?: string | null;
  arrivedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  // Denormalised context, so a worklist row renders without a second call.
  accessionNumber?: string;
  studyInstanceUID?: string;
  requestedProcedureId?: string;
  requestedProcedureDescription?: string;
  requestedProcedurePriority?: string;
  referringPhysicianName?: string;
  requestingPhysician?: string;
  patient?: MwlPatient;
}

export interface MwlOrder extends Record<string, unknown> {
  id?: number;
  accessionNumber?: string;
  patientId?: number;
  patient?: MwlPatient;
  studyInstanceUID?: string;
  requestedProcedureId?: string;
  requestedProcedureDescription?: string;
  requestedProcedurePriority?: string;
  reasonForRequestedProcedure?: string;
  requestingPhysician?: string;
  requestingService?: string;
  referringPhysicianName?: string;
  admissionId?: string;
  currentPatientLocation?: string;
  placerOrderNumber?: string;
  fillerOrderNumber?: string;
  internalNotes?: string;
  status?: OrderStatus;
  source?: string;
  scheduledProcedureSteps?: MwlScheduledStep[];
  createdAt?: string;
  updatedAt?: string;
}

export interface WorklistFilters {
  patientId?: string;
  patientName?: string;
  patientSex?: string;
  accessionNumber?: string;
  studyInstanceUID?: string;
  modality?: string;
  scheduledStationAeTitle?: string;
  performingPhysicianName?: string;
  requestedProcedureId?: string;
  requestingPhysician?: string;
  admissionId?: string;
  startDateFrom?: string;
  startDateTo?: string;
  startTimeFrom?: string;
  startTimeTo?: string;
  status?: SpsStatus[];
  search?: string;
  page?: number;
  size?: number;
}

export interface Paged<T> {
  items: T[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
}

export interface WorklistProvider {
  id: number;
  aeTitle: string;
  name: string | null;
  deviceId: number | null;
  deviceName: string | null;
  status: string | null;
  worklistEnabled: boolean;
  /** Split out so a half-configured AE shows as such, not just "off". */
  worklistServiceEnabled: boolean;
  worklistCapabilityEnabled: boolean;
}

/** Drop empty filter values: a blank string is a matching key, not "no filter". */
function toParams(filters: WorklistFilters = {}): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (Array.isArray(value) && value.length === 0) {
      return;
    }
    params[key] = value;
  });
  return params;
}

// ============================ Worklist ============================

export function useWorklist(filters: WorklistFilters = {}, config = {}) {
  const params = toParams(filters);
  return useFetch<Paged<MwlScheduledStep>>(
    async () => {
      const response = await apiClient.get('/mwl/worklist', params);
      return response.data;
    },
    mwlQueryKeys.worklist(params),
    config
  );
}

export function useWorklistStats(config = {}) {
  return useFetch<any>(
    async () => {
      const response = await apiClient.get('/mwl/stats');
      return response.data;
    },
    mwlQueryKeys.stats,
    config
  );
}

// ============================ Orders ============================

export function useOrders(
  filters: { search?: string; accessionNumber?: string; status?: OrderStatus[]; page?: number; size?: number } = {},
  config = {}
) {
  const params = toParams(filters as WorklistFilters);
  return useFetch<Paged<MwlOrder>>(
    async () => {
      const response = await apiClient.get('/mwl/orders', params);
      return response.data;
    },
    mwlQueryKeys.orders(params),
    config
  );
}

export function useOrder(orderId: number | null, config = {}) {
  return useFetch<{ order: MwlOrder }>(
    async () => {
      const response = await apiClient.get(`/mwl/orders/${orderId}`);
      return response.data;
    },
    orderId ? mwlQueryKeys.order(orderId) : null,
    { enabled: !!orderId, ...config }
  );
}

export function useCreateOrder(
  onSuccess?: (response: AxiosResponse, data: MwlOrder) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: MwlOrder) => apiClient.post('/mwl/orders', data),
    [...mwlQueryKeys.all],
    'Failed to place the order',
    'Order placed',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useUpdateOrder(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { orderId: number; order: MwlOrder }) =>
      apiClient.put(`/mwl/orders/${data.orderId}`, data.order),
    [...mwlQueryKeys.all],
    'Failed to update the order',
    'Order updated',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useCancelOrder(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { orderId: number; reason?: string }) =>
      apiClient.post(`/mwl/orders/${data.orderId}/cancel`, { reason: data.reason }),
    [...mwlQueryKeys.all],
    'Failed to cancel the order',
    'Order cancelled',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useDeleteOrder(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { orderId: number }) => apiClient.delete(`/mwl/orders/${data.orderId}`),
    [...mwlQueryKeys.all],
    'Failed to delete the order',
    'Order deleted',
    undefined,
    onSuccess,
    onError,
    notification
  );
}


export function useAddScheduledStep(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { orderId: number; step: MwlScheduledStep }) =>
      apiClient.post(`/mwl/orders/${data.orderId}/steps`, data.step),
    [...mwlQueryKeys.all],
    'Failed to schedule the exam',
    'Exam scheduled',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useUpdateScheduledStep(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { stepId: number; step: MwlScheduledStep }) =>
      apiClient.put(`/mwl/steps/${data.stepId}`, data.step),
    [...mwlQueryKeys.all],
    'Failed to update the scheduled exam',
    'Exam updated',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useChangeStepStatus(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { stepId: number; status: SpsStatus; reason?: string }) =>
      apiClient.post(`/mwl/steps/${data.stepId}/status`, {
        status: data.status,
        reason: data.reason,
      }),
    [...mwlQueryKeys.all],
    'Failed to change the exam status',
    'Exam status updated',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useDeleteScheduledStep(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { stepId: number }) => apiClient.delete(`/mwl/steps/${data.stepId}`),
    [...mwlQueryKeys.all],
    'Failed to remove the scheduled exam',
    'Scheduled exam removed',
    undefined,
    onSuccess,
    onError,
    notification
  );
}


export function useMwlPatients(
  filters: { search?: string; patientId?: string; activeOnly?: boolean; page?: number; size?: number } = {},
  config = {}
) {
  const params = toParams(filters as WorklistFilters);
  return useFetch<Paged<MwlPatient>>(
    async () => {

      const response = await apiClient.get('/mwl/patients', params);
      return response.data;
    },
    mwlQueryKeys.patients(params),
    config
  );
}

export function useCreateMwlPatient(
  onSuccess?: (response: AxiosResponse, data: MwlPatient) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: MwlPatient) => apiClient.post('/mwl/patients', data),
    [...mwlQueryKeys.all],
    'Failed to register the patient',
    'Patient registered',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useUpdateMwlPatient(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { patientId: number; patient: MwlPatient }) =>
      apiClient.put(`/mwl/patients/${data.patientId}`, data.patient),
    [...mwlQueryKeys.all],
    'Failed to update the patient',
    'Patient updated',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useMergeMwlPatients(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { losingPatientId: number; survivingPatientId: number }) =>
      apiClient.post(`/mwl/patients/${data.losingPatientId}/merge`, {
        survivingPatientId: data.survivingPatientId,
      }),
    [...mwlQueryKeys.all],
    'Failed to merge the patient records',
    'Patient records merged',
    undefined,
    onSuccess,
    onError,
    notification
  );
}


export function useWorklistProviders(config = {}) {
  return useFetch<{ applicationEntities: WorklistProvider[]; count: number }>(
    async () => {
      const response = await apiClient.get('/mwl/application-entities');
      return response.data;
    },
    mwlQueryKeys.providers,
    config
  );
}

export function useToggleWorklistProvider(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { aeId: number; enable: boolean }) =>
      apiClient.post(`/mwl/application-entities/${data.aeId}/${data.enable ? 'enable' : 'disable'}`),
    [...mwlQueryKeys.all],
    'Failed to change the worklist setting',
    'Worklist setting updated',
    undefined,
    onSuccess,
    onError,
    notification
  );
}


export interface WorklistSource {
  id: number;
  deviceId: number | null;
  aeTitle: string;
  callingAeTitle: string;
  hostname: string;
  port: number;
  tlsEnabled: boolean;
  label: string;
  /** It can answer a Modality Worklist C-FIND. */
  supportsWorklist: boolean;
  /** It can answer a study or patient level C-FIND. */
  supportsStudyQuery: boolean;
}


export function useWorklistSources(
  capability: 'worklist' | 'study' | 'all' = 'worklist',
  config = {}
) {
  return useFetch<{ destinations: WorklistSource[]; count: number }>(
    async () => {
      const response = await apiClient.get('/mwl/remote/destinations', {
        capability,
      });
      return response.data;
    },
    ['mwl', 'remote-destinations', capability],
    config
  );
}

export function useToggleDestinationQuery(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: {
      aeId: number;
      enable: boolean;
      worklist?: boolean;
      studyQuery?: boolean;
    }) =>
      apiClient.post(
        `/mwl/remote/destinations/${data.aeId}/${data.enable ? 'enable' : 'disable'}`,
        null,
        data.enable
          ? {
              params: {
                worklist: data.worklist ?? true,
                studyQuery: data.studyQuery ?? false,
              },
            }
          : undefined
      ),
    [...mwlQueryKeys.all],
    'Failed to change the query setting',
    'Query setting updated',
    undefined,
    onSuccess,
    onError,
    notification
  );
}


export interface RemoteWorklistRequest extends Record<string, unknown> {
  patientId?: string;
  patientName?: string;
  accessionNumber?: string;
  modality?: string;
  scheduledStationAeTitle?: string;
  startDateFrom?: string;
  startDateTo?: string;
  limit?: number;
}

/** Read-only: looks at another system's worklist without storing anything. */
export function useQueryRemoteWorklist(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = false
) {
  return useGenericMutation(
    async (data: { aeId: number; query: RemoteWorklistRequest }) =>
      apiClient.post(`/mwl/remote/${data.aeId}/query`, data.query),
    [...mwlQueryKeys.all],
    'Failed to query the remote worklist',
    'Remote worklist retrieved',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useImportRemoteWorklist(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = true
) {
  return useGenericMutation(
    async (data: { aeId: number; query: RemoteWorklistRequest }) =>
      apiClient.post(`/mwl/remote/${data.aeId}/import`, data.query),
    [...mwlQueryKeys.all],
    'Failed to import the remote worklist',
    'Worklist imported',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useDicomQuery(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification = false
) {
  return useGenericMutation(
    async (data: {
      aeId: number;
      level: 'PATIENT' | 'STUDY' | 'SERIES' | 'IMAGE' | 'WORKLIST';
      keys?: Record<string, string>;
      returnKeys?: string[];
      limit?: number;
    }) =>
      apiClient.post(`/mwl/query/${data.aeId}`, {
        level: data.level,
        keys: data.keys,
        returnKeys: data.returnKeys,
        limit: data.limit,
      }),
    [...mwlQueryKeys.all],
    'Failed to run the query',
    'Query completed',
    undefined,
    onSuccess,
    onError,
    notification
  );
}
